import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ArrowRight, Apple, Bell, Mail, MessageCircle, MessageSquare,
  Phone, PhoneCall, ShieldCheck, Store, Zap
} from 'lucide-react';

/**
 * Channels & Signup — how merchants join and how the OS reaches them.
 * The single honest reference for signup paths, channel economics, and
 * the severity-based outreach policy.
 */
export default function DocsChannelsSignup() {
  const signupPaths = [
    {
      icon: MessageCircle, color: '#25D366', name: 'WhatsApp (primary)',
      status: 'Live',
      desc: 'Message our WhatsApp number — that IS the signup. Your first message is a verified phone (Meta authenticated it), so there\'s no OTP and no form. It also opens a free 24-hour service window, so onboarding costs nothing.',
    },
    {
      icon: MessageSquare, color: '#3EF1C7', name: 'SMS text-in',
      status: 'Live',
      desc: 'Text our number from any phone and you\'re signed up. This is also the automatic fallback when an iPhone user\'s iMessage isn\'t deliverable — it silently becomes an SMS and still works.',
    },
    {
      icon: Apple, color: '#007AFF', name: 'iMessage',
      status: 'Beta',
      desc: 'Data-based (free per message), connected through a relay — a Mac signed into Messages.app or a hosted vendor. Same inbound-first signup once a relay is deployed. Apple-ID email handles link to existing accounts by email match.',
    },
    {
      icon: Phone, color: '#94A3B8', name: 'Phone number + OTP',
      status: 'Live',
      desc: 'The classic path from the website: enter your number, get a 6-digit code (WhatsApp first, SMS fallback). Kept for people who prefer starting from the browser.',
    },
    {
      icon: Store, color: '#7A5CFF', name: 'Shopify App Store',
      status: 'Live',
      desc: 'Install from the App Store — OAuth detects your store automatically, and connecting store data jumps you +100 spots in the onboarding queue.',
    },
  ];

  const outreach = [
    {
      severity: 'Routine', color: '#94A3B8',
      examples: 'Digests, stories, onboarding tips',
      policy: 'Email (free). If no email: WhatsApp only while your free reply window is open — never a paid template for routine messages.',
    },
    {
      severity: 'Actionable', color: '#FFD166',
      examples: 'Approvals, Money Found statements, fix alerts',
      policy: 'WhatsApp free-form inside the 24h window; an approved utility template (~half a cent) only when the window is closed; email fallback.',
    },
    {
      severity: 'Critical (Sev-1)', color: '#F55454',
      examples: 'Runway critical, payment failure, store down',
      policy: 'WhatsApp template regardless of window PLUS an automated phone call. You will not miss it.',
    },
  ];

  const keywords = [
    ['STOP / UNSUBSCRIBE', 'Opt out of everything (honored instantly, carrier-compliant)'],
    ['START', 'Opt back in'],
    ['HELP', 'Help text with all commands'],
    ['PAUSE', 'Mute onboarding tips only — statements and urgent alerts continue'],
    ['SMS / WHATSAPP', 'Switch your preferred channel'],
    ['"talk to supply chain"', 'Reach any business department by name'],
  ];

  return (
    <div className="max-w-4xl">
      {/* Hero */}
      <div className="mb-12">
        <Badge className="bg-[#3EF1C7]/10 text-[#3EF1C7] border-[#3EF1C7]/30 mb-4">
          Messaging & OpenClaw
        </Badge>
        <h1 className="text-3xl font-bold text-white mb-4">Channels & Signup</h1>
        <p className="text-[#94A3B8] text-lg">
          How merchants join Commerce Index, and how the OS reaches them — designed
          so the everyday flow costs nothing: your message to us opens a free reply
          window, email carries the routine traffic, and paid messages are reserved
          for things worth paying for.
        </p>
      </div>

      {/* Signup paths */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">Five ways in</h2>
        <div className="space-y-4">
          {signupPaths.map(p => (
            <Card key={p.name} className="bg-[#0D1628] border-[#22304A]">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                       style={{ background: `${p.color}1A` }}>
                    <p.icon className="w-5 h-5" style={{ color: p.color }} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-white font-semibold">{p.name}</h3>
                      <Badge className={p.status === 'Live'
                        ? 'bg-[#3EF1C7]/10 text-[#3EF1C7] border-[#3EF1C7]/30'
                        : 'bg-[#FFD166]/10 text-[#FFD166] border-[#FFD166]/30'}>
                        {p.status}
                      </Badge>
                    </div>
                    <p className="text-[#94A3B8] text-sm">{p.desc}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Outreach policy */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-2">
          <span className="flex items-center gap-2"><Bell className="w-5 h-5 text-[#3EF1C7]" /> How the OS reaches you</span>
        </h2>
        <p className="text-[#94A3B8] text-sm mb-6">
          Every outbound message is routed by urgency through the channel policy —
          the cheapest channel that fits how much the message matters.
        </p>
        <div className="space-y-4">
          {outreach.map(o => (
            <Card key={o.severity} className="bg-[#0D1628] border-[#22304A]">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: o.color }} />
                  <h3 className="text-white font-semibold">{o.severity}</h3>
                  <span className="text-xs text-[#64748B]">— {o.examples}</span>
                </div>
                <p className="text-[#94A3B8] text-sm">{o.policy}</p>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="mt-4 flex items-start gap-2 text-sm text-[#94A3B8]">
          <PhoneCall className="w-4 h-4 text-[#F55454] mt-0.5 flex-shrink-0" />
          <span>
            Sev-1 voice calls are config-gated and rare by design (~$0.014/min).
            Everything else runs on Meta's Cloud API and email — Twilio is optional.
          </span>
        </div>
      </section>

      {/* Keywords */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          <span className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#3EF1C7]" /> Control words</span>
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-6">
            <div className="space-y-3">
              {keywords.map(([word, meaning]) => (
                <div key={word} className="flex items-start gap-3 text-sm">
                  <code className="text-[#3EF1C7] whitespace-nowrap">{word}</code>
                  <span className="text-[#94A3B8]">{meaning}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Cost table */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          <span className="flex items-center gap-2"><Zap className="w-5 h-5 text-[#3EF1C7]" /> Channel economics</span>
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[#64748B] border-b border-[#22304A]">
                <th className="py-2 pr-4">Channel</th>
                <th className="py-2 pr-4">Per message</th>
                <th className="py-2">Notes</th>
              </tr>
            </thead>
            <tbody className="text-[#94A3B8]">
              <tr className="border-b border-[#22304A]/50">
                <td className="py-2 pr-4 text-white">WhatsApp (in window)</td>
                <td className="py-2 pr-4 text-[#3EF1C7]">Free</td>
                <td className="py-2">Your message opens a rolling 24h free-reply window</td>
              </tr>
              <tr className="border-b border-[#22304A]/50">
                <td className="py-2 pr-4 text-white">WhatsApp template</td>
                <td className="py-2 pr-4">~$0.005–0.02</td>
                <td className="py-2">Only for urgent pushes outside the window</td>
              </tr>
              <tr className="border-b border-[#22304A]/50">
                <td className="py-2 pr-4 text-white">Email</td>
                <td className="py-2 pr-4 text-[#3EF1C7]">~Free</td>
                <td className="py-2">Default for routine traffic</td>
              </tr>
              <tr className="border-b border-[#22304A]/50">
                <td className="py-2 pr-4 text-white">iMessage</td>
                <td className="py-2 pr-4 text-[#3EF1C7]">Free</td>
                <td className="py-2">Data-based; relay (Mac or hosted) is the only cost</td>
              </tr>
              <tr className="border-b border-[#22304A]/50">
                <td className="py-2 pr-4 text-white">SMS</td>
                <td className="py-2 pr-4">~$0.008</td>
                <td className="py-2">OTP fallback + text-in signup; plan allowances apply</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 text-white">Voice (Sev-1)</td>
                <td className="py-2 pr-4">~$0.014/min</td>
                <td className="py-2">Critical alerts only</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-xs text-[#64748B] mt-3">
          Each plan includes a monthly message allowance (Free 30 · Starter 500 ·
          Pro 2,000 · Scale 10,000); beyond it, billable messages meter in credits
          at carrier cost plus a small volume-tiered fee. OTP and STOP/HELP traffic
          is never billed.
        </p>
      </section>

      {/* Next */}
      <div className="flex flex-wrap gap-3">
        <Link to="/docs/channel-setup"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#3EF1C7] text-[#0B1120] text-sm font-semibold hover:bg-[#3EF1C7]/90">
          Channel setup guides <ArrowRight className="w-4 h-4" />
        </Link>
        <Link to="/docs/commands"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#22304A] text-white text-sm font-semibold hover:bg-[#1E293B]">
          Command reference
        </Link>
      </div>
    </div>
  );
}
